import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import EditorialMotif from "../EditorialMotif";
import {
  fetchMarkers,
  normalizeFilterValue,
  subscribeToMarkers,
} from "./markersData";

const formatFilterLabel = (value) => {
  const trimmed = String(value || "").trim();
  if (/^[A-Z]{2,5}$/.test(trimmed)) return trimmed;

  return normalizeFilterValue(trimmed).replace(/\b\w/g, (letter) =>
    letter.toUpperCase()
  );
};

const buildFilterOptions = (values) => {
  const options = new Map();

  values.filter(Boolean).forEach((value) => {
    const normalized = normalizeFilterValue(value);
    if (normalized && normalized !== "unknown" && !options.has(normalized)) {
      options.set(normalized, formatFilterLabel(value));
    }
  });

  return [...options.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

function MapSidebar({
  sidebarOpen,
  onCloseSidebar,
  search,
  onSearchChange,
  onMarkerHover,
  user,
  onAddLocation,
  userUnlockedMarkers = [],
  onShowCheckout,
  filters,
  onFiltersChange,
}) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMarkers();

    const unsubscribe = subscribeToMarkers(user, (change) => {
      if (["added", "modified", "removed"].includes(change.type)) {
        loadMarkers();
      }
    });

    return () => unsubscribe();
  }, [user, userUnlockedMarkers]);

  const loadMarkers = async () => {
    setLoading(true);
    try {
      const markersData = await fetchMarkers(user, userUnlockedMarkers);
      setMarkers(markersData);
    } catch (error) {
      console.error("Error loading markers:", error);
    } finally {
      setLoading(false);
    }
  };

  const activeFilters = filters || {
    area: "all",
    type: "all",
    verification: "all",
  };
  const areas = buildFilterOptions(markers.map((marker) => marker.area));
  const types = buildFilterOptions(markers.map((marker) => marker.type));

  const updateFilter = (key, value) => {
    onFiltersChange?.({ ...activeFilters, [key]: value });
  };

  const filteredMarkers = markers.filter((marker) => {
    const query = search.toLowerCase();
    const matchesSearch = [marker.name, marker.type, marker.area]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query));
    const matchesArea =
      activeFilters.area === "all" ||
      normalizeFilterValue(marker.area) === activeFilters.area;
    const matchesType =
      activeFilters.type === "all" ||
      normalizeFilterValue(marker.type) === activeFilters.type;
    const matchesVerification =
      !user?.premium ||
      activeFilters.verification === "all" ||
      marker.verificationStatus === activeFilters.verification;

    return matchesSearch && matchesArea && matchesType && matchesVerification;
  });

  const freshMarkers = filteredMarkers.filter(
    (marker) => marker.verificationStatus === "fresh"
  );
  const reviewMarkers = filteredMarkers.filter(
    (marker) => marker.verificationStatus !== "fresh"
  );

  const sidebarClasses = `croatoan-sidebar sidebar-scroll fixed md:static top-0 left-0 z-[10000] h-full transition-transform duration-300 ${
    sidebarOpen ? "translate-x-0" : "-translate-x-full"
  } md:translate-x-0`;

  if (loading) {
    return (
      <aside className={sidebarClasses} aria-live="polite">
        <div className="classified-loading">
          <span className="classified-loading__code">Croatoan</span>
          <span className="classified-loading__line" />
          <strong>Loading locations</strong>
        </div>
      </aside>
    );
  }

  return (
    <aside className={sidebarClasses} aria-label="Croatoan location archive">
      <button
        className="sidebar-close md:hidden"
        onClick={onCloseSidebar}
        aria-label="Close location archive"
      >
        <X size={20} />
      </button>

      <header className="sidebar-masthead">
        <EditorialMotif className="sidebar-motif" />
        <div className="classification-tape">
          <span>Curated places</span>
          <span>Member map</span>
        </div>

        <div className="sidebar-brand-row">
          <img src="/logo.png" alt="" className="sidebar-logo" />
          <div>
            <p className="sidebar-eyebrow">Private urban discovery</p>
            <h1>Croatoan</h1>
          </div>
          <span className="system-beacon" title="Map is live" />
        </div>

        <div className="sidebar-rule" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </header>

      <section className="sidebar-section" aria-labelledby="archive-actions">
        <div className="section-identifier">
          <span>01</span>
          <p id="archive-actions">Contribute</p>
        </div>
        <button onClick={onAddLocation} className="classified-primary-button">
          <Plus size={16} />
          Share a location
        </button>
      </section>

      <section className="sidebar-section" aria-labelledby="archive-query">
        <div className="section-identifier">
          <span>02</span>
          <p id="archive-query">Explore</p>
        </div>

        <label className="classified-field">
          <span>Search</span>
          <input
            type="search"
            placeholder="SEARCH LOCATIONS"
            value={search}
            onChange={onSearchChange}
          />
        </label>

        <div className="sidebar-filter-grid">
          <label className="classified-field">
            <span>Area</span>
            <select
              value={activeFilters.area}
              onChange={(event) => updateFilter("area", event.target.value)}
            >
              <option value="all">All areas</option>
              {areas.map((area) => (
                <option key={area.value} value={area.value}>
                  {area.label}
                </option>
              ))}
            </select>
          </label>

          <label className="classified-field">
            <span>Location type</span>
            <select
              value={activeFilters.type}
              onChange={(event) => updateFilter("type", event.target.value)}
            >
              <option value="all">All types</option>
              {types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {user?.premium ? (
          <label className="classified-field verification-filter-field">
            <span>Verification age · Premium</span>
            <select
              value={activeFilters.verification}
              onChange={(event) =>
                updateFilter("verification", event.target.value)
              }
            >
              <option value="all">All verification states</option>
              <option value="fresh">Fresh · under 60 days</option>
              <option value="aging">Review · 60–119 days</option>
              <option value="stale">Stale · 120+ days</option>
            </select>
          </label>
        ) : (
          <button
            type="button"
            className="premium-filter-lock"
            onClick={onShowCheckout}
          >
            <span>Verification-age filters</span>
            <strong>Unlock with Premium →</strong>
          </button>
        )}
      </section>

      <div className="archive-metrics" aria-label="Filtered location counts">
        <div>
          <strong>{String(filteredMarkers.length).padStart(2, "0")}</strong>
          <span>Indexed</span>
        </div>
        <div>
          <strong>{String(freshMarkers.length).padStart(2, "0")}</strong>
          <span>Fresh</span>
        </div>
        <div>
          <strong>{String(reviewMarkers.length).padStart(2, "0")}</strong>
          <span>Review</span>
        </div>
      </div>

      <section className="classified-location-list" aria-labelledby="field-records">
        <div className="location-list-heading">
          <span id="field-records">03 / Locations</span>
          <span>{filteredMarkers.length} showing</span>
        </div>

        {filteredMarkers.length > 0 ? (
          filteredMarkers.map((marker, index) => {
            const isUnlocked = userUnlockedMarkers.includes(marker.id);
            const verificationStatus = marker.verificationStatus || marker.status;
            const recordLabel =
              verificationStatus === "fresh"
                ? "Fresh"
                : verificationStatus === "aging"
                ? "60–119 days"
                : "120+ days";

            return (
              <article className="classified-location-card" key={marker.id}>
                <button type="button" onClick={() => onMarkerHover(marker)}>
                  <div className="location-card-media">
                    {marker.images?.[0] ? (
                      <img
                        src={marker.images[0]}
                        alt=""
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="location-card-placeholder">NO VISUAL</div>
                    )}
                    <span className="location-card-index">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="location-card-crosshair" aria-hidden="true" />
                  </div>

                  <div className="location-card-copy">
                    <div>
                      <span>{marker.area || "Area unknown"}</span>
                      <span>{marker.type || "Location"}</span>
                    </div>
                    <h2>{marker.name}</h2>
                    {marker.desc && <p>{marker.desc}</p>}
                    <footer>
                      <span className="location-record-tags">
                        <span
                          className={`record-status record-status--${verificationStatus}`}
                        >
                          {recordLabel}
                        </span>
                        {marker.is_premium && (
                          <span className="record-tier">
                            {isUnlocked ? "Unlocked" : "Premium"}
                          </span>
                        )}
                      </span>
                      <span className="record-open">View location →</span>
                    </footer>
                  </div>
                </button>
              </article>
            );
          })
        ) : (
          <div className="archive-empty-state">
            <span>00</span>
            <p>{search ? "No matching locations" : "No locations yet"}</p>
          </div>
        )}
      </section>

      <footer className="sidebar-footer">
        <div className="section-identifier">
          <span>04</span>
          <p>Membership</p>
        </div>
        <div className="operator-status">
          <div>
            <span>Credits</span>
            <strong>{user?.points || 0}</strong>
          </div>
          <div>
            <span>Plan</span>
            <strong>{user?.premium ? "Premium" : "Standard"}</strong>
          </div>
        </div>

        <button onClick={onShowCheckout} className="classified-secondary-button">
          {user?.premium ? "Manage membership" : "Explore Premium"}
        </button>

        <div className="sidebar-network-links">
          <span>Community</span>
          <div>
            <a
              href="https://discord.gg/JMSEXJdWdC"
              target="_blank"
              rel="noreferrer"
              aria-label="Croatoan Discord"
            >
              <i className="fa-brands fa-discord" />
            </a>
            <a
              href="https://www.instagram.com/croatoan.map/?hl=en"
              target="_blank"
              rel="noreferrer"
              aria-label="Croatoan Instagram"
            >
              <i className="fa-brands fa-instagram" />
            </a>
          </div>
        </div>
      </footer>
    </aside>
  );
}

export default MapSidebar;
